import * as _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Actions, IRowData } from './models';
import { orderByPreSelectDefault } from '../potential-donors/models/default-sort';
import { IListItem, IPreselectDonorInfo, IRecipient } from '@nkr/models/index';
import { DonorPreselectReviewStatus, PreSelectSection, PreSelectSubSection } from '@nkr/enums/index';
import { GridReadyEvent } from 'ag-grid-community';
import { Panel } from 'react-bootstrap';
import PopoverTemplate from '@nkr/components/popover-template';
import { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import RecipientInfo from '../potential-donors/recipient-info';
import PotentialDonorList from '../potential-donors/list2';

export const getWhichKidney = (donor: IPreselectDonorInfo) => {
    let arteryCount = donor.numberOfArteries;
    let arteryLabel = arteryCount > 1 ? 'arteries' : 'artery';

    switch (donor.whichKidney) {
        case 'Left': return <p>Left kidney with ({arteryCount}) {arteryLabel}</p>;
        case 'Right': return <p>Right kidney with ({arteryCount}) {arteryLabel}</p>;
        case 'L-opt': return <p>Left kidney with ({arteryCount}) {arteryLabel} but right kidney is available</p>;
        case 'R-opt': return <p>Right kidney with ({arteryCount}) {arteryLabel} but left kidney is available</p>;
    }
}

export const getOverlayForStatus = (data: IRowData) => {
    return (
        <PopoverTemplate>
            <div style={{ minWidth: 400 }}>
                {data.donorNote != null && (
                    <>
                        <div><strong>Donor Note</strong></div>
                        <div>{data.donorNote != null ? data.donorNote.note : ''}</div>
                    </>
                )}

                {data.matchNote != null && (
                    <>
                        <div><strong>Match Note</strong></div>
                        <div>{data.matchNote != null ? data.matchNote.note : ''}</div>
                    </>
                )}
            </div>
        </PopoverTemplate>
    );
};

interface IProps {
    recipient: IRecipient,
    list: IRowData[],
    groupIsSuper: boolean,
    recipientCount: number,
    hardBlockReasonList: IListItem[],
    hardBlockReasonChoices: string[],
    donorPreselectReviewStatusList: IListItem[],
    statusFilter: number,
    fetching: boolean,
    handleAction: (recipientId: number, donorId: number, action: Actions) => void,
    handleReviewStatusChange: (recipientId: number, donorId: number, reviewType: DonorPreselectReviewStatus) => void,
    handleSortChange: (sortByAccessor: string, sortByDesc: boolean) => void,
    handleMultiHardblock: (isBulkUpdate?: boolean) => void,
    handleSelectAll: (recipientId: number) => void,
    handleSelected: (recipientId: number, donorId: number, checked: boolean) => void,
    handleBulkUpdate: (recipientId: number, bulkUpdateAction: string, isAccepted: boolean, isFullyConfident: boolean, hardBlockReason: number, reviewStatus: DonorPreselectReviewStatus) => void,
    handleShowMortalityBreakdown: (recipient: IRecipient) => void,
    handleShowFwt: (recipient: IRecipient) => void
}

const PreselectList = (props: IProps) => {
    const grid = useRef<AgGridReactType>(null)
    const [statusFilter, setStatusFilter] = useState(0);

    const onBulkUpdate = (bulkUpdateAction: string, isAccepted: boolean, isFullyConfident: boolean, hardBlockReason: number, reviewStatus: DonorPreselectReviewStatus) => {
        props.handleBulkUpdate(props.recipient.id, bulkUpdateAction, isAccepted, isFullyConfident, hardBlockReason, reviewStatus);
    };

    const onSelectAll = () => {
        props.handleSelectAll(props.recipient.id);
    }

    useEffect(() => {
        if (props.fetching == false && grid != null) {
            if (props.list.length == 0) {
                grid.current.api.showNoRowsOverlay();
            }
        }
    }, [props.fetching, grid, props.list]);

    const filteredList = props.list.filter(x => {
        let isStatusFilter = statusFilter > 0 ? x.reviewStatus == statusFilter : true;

        return isStatusFilter;
    });

    // Same default order as the recipient pre-select grid, applied within this recipient's
    // panel: pending first, then accepted, then declined.
    const list = useMemo(() => orderByPreSelectDefault(filteredList), [props.list, statusFilter]);

    let height = null;

    if (props.list.length >= 10) {
        height = '350px';
    } else {
        height = null;
    }

    let l: IRowData[] = props.list.map(item => {
        return {
            ...item,
            donor: item.donor,
            isSelected: false
        };
    });

    let pairedDonors = l.filter(x => x.isPairedDonor && x.donor.isDeleted == false);

    return (
        <>
            <Panel bsStyle='info' style={{ border: '0 none' }}>
                <Panel.Heading style={{ paddingTop: 2, paddingBottom: 2 }}>
                    <RecipientInfo
                        recipient={props.recipient}
                        waitEstimate={props.recipient.waitEstimate}
                        recipientFwt={props.recipient.fwtString}
                        declineWait={props.recipient.declineWait}
                        declinePercent={props.recipient.declinePercent}
                        isPreselectPage={true}
                        list={list}
                        onShowMortalityBreakdown={props.handleShowMortalityBreakdown}
                        onShowFWTBreakdown={props.handleShowFwt}
                    />
                </Panel.Heading>

                <Panel.Body style={{ padding: 0 }}>
                    <PotentialDonorList
                        list={list}
                        pairedDonors={pairedDonors}
                        centerId={props.recipient.centerId}
                        recipient={props.recipient}
                        pendingMatchHistoryDonorId={props.recipient.pendingMatchHistoryDonorId}
                        hardBlockReasons={props.hardBlockReasonList}
                        hardBlockReasonChoices={props.hardBlockReasonChoices}
                        donorPreselectReviewStatusList={props.donorPreselectReviewStatusList}
                        statusFilter={props.statusFilter}
                        groupIsSuper={props.groupIsSuper}
                        section={PreSelectSection.PotentialDonors}
                        subSection={PreSelectSubSection.Pending}
                        isPreselectPage={true}
                        onSortChange={props.handleSortChange}
                        onAction={props.handleAction}
                        onMultiHardblock={props.handleMultiHardblock}
                        onSelectAll={onSelectAll}
                        onSelected={props.handleSelected}
                        onBulkUpdate={onBulkUpdate}
                        onReviewStatusChange={props.handleReviewStatusChange}
                    />
                </Panel.Body>
            </Panel>
        </>
    );
}

export default PreselectList;